import { 
  UsersIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ChartBarIcon
} from "@heroicons/react/24/outline";
import type { ProjectResource } from "@/features/Projects/models/project.types";
import type { ResourcesSummaryProps } from "../resourcesPhase.types";
import { RESOURCE_TYPE_CONFIG } from "../resourcesPhase.definitions";

export function ResourcesSummary({ resources, estimatedPrice }: ResourcesSummaryProps) {
  const totalHours = resources.reduce((sum, resource) => sum + resource.hoursEstimated, 0);
  const averageHourlyRate = totalHours > 0 ? Math.round(estimatedPrice / totalHours) : 0;

  // Group resources by type
  const resourcesByType = resources.reduce((acc, resource) => {
    if (!acc[resource.type]) {
      acc[resource.type] = [];
    }
    acc[resource.type].push(resource);
    return acc;
  }, {} as Record<string, ProjectResource[]>);

  const typeStats = Object.entries(resourcesByType).map(([type, typeResources]) => {
    const totalCost = typeResources.reduce((sum, r) => sum + r.totalCost, 0);
    const totalHours = typeResources.reduce((sum, r) => sum + r.hoursEstimated, 0);
    const percentage = estimatedPrice > 0 ? Math.round((totalCost / estimatedPrice) * 100) : 0;
    
    return {
      type,
      count: typeResources.length,
      totalCost,
      totalHours,
      percentage,
      config: RESOURCE_TYPE_CONFIG[type]
    };
  }).sort((a, b) => b.totalCost - a.totalCost);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
      <div className="flex items-center space-x-2 mb-6">
        <ChartBarIcon className="h-5 w-5 text-pink-600" />
        <h3 className="text-lg font-medium text-gray-900">
          Resumo dos Recursos
        </h3>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <UsersIcon className="h-5 w-5 text-blue-600" />
            <div>
              <div className="text-2xl font-bold text-blue-900">{resources.length}</div>
              <div className="text-sm text-blue-700">Recursos</div>
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <ClockIcon className="h-5 w-5 text-green-600" />
            <div>
              <div className="text-2xl font-bold text-green-900">{totalHours}</div>
              <div className="text-sm text-green-700">Total de Horas</div>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CurrencyDollarIcon className="h-5 w-5 text-purple-600" />
            <div>
              <div className="text-2xl font-bold text-purple-900">
                R$ {estimatedPrice.toLocaleString('pt-BR')}
              </div>
              <div className="text-sm text-purple-700">Custo Total</div>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <ChartBarIcon className="h-5 w-5 text-orange-600" />
            <div>
              <div className="text-2xl font-bold text-orange-900">
                R$ {averageHourlyRate}
              </div>
              <div className="text-sm text-orange-700">Média/Hora</div>
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown by Type */}
      <div>
        <h4 className="font-medium text-gray-900 mb-4">Distribuição por Tipo</h4>
        <div className="space-y-3">
          {typeStats.map((stat) => (
            <div key={stat.type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`
                  w-8 h-8 rounded-lg flex items-center justify-center
                  ${stat.config?.color === 'blue' ? 'bg-blue-100' :
                    stat.config?.color === 'purple' ? 'bg-purple-100' :
                    stat.config?.color === 'green' ? 'bg-green-100' :
                    stat.config?.color === 'orange' ? 'bg-orange-100' :
                    stat.config?.color === 'indigo' ? 'bg-indigo-100' :
                    'bg-gray-100'}
                `}>
                  {stat.config?.icon && (
                    <stat.config.icon className={`
                      w-4 h-4
                      ${stat.config?.color === 'blue' ? 'text-blue-600' :
                        stat.config?.color === 'purple' ? 'text-purple-600' :
                        stat.config?.color === 'green' ? 'text-green-600' :
                        stat.config?.color === 'orange' ? 'text-orange-600' :
                        stat.config?.color === 'indigo' ? 'text-indigo-600' :
                        'text-gray-600'}
                    `} />
                  )}
                </div>
                
                <div>
                  <div className="font-medium text-gray-900">
                    {stat.config?.label || stat.type}
                  </div>
                  <div className="text-sm text-gray-600">
                    {stat.count} recurso{stat.count !== 1 ? 's' : ''} • {stat.totalHours}h
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="font-medium text-gray-900">
                    R$ {stat.totalCost.toLocaleString('pt-BR')}
                  </div>
                  <div className="text-sm text-gray-500">
                    {stat.percentage}% do total
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="w-16 bg-gray-200 rounded-full h-2">
                  <div 
                    className={`
                      h-2 rounded-full
                      ${stat.config?.color === 'blue' ? 'bg-blue-500' :
                        stat.config?.color === 'purple' ? 'bg-purple-500' :
                        stat.config?.color === 'green' ? 'bg-green-500' :
                        stat.config?.color === 'orange' ? 'bg-orange-500' :
                        stat.config?.color === 'indigo' ? 'bg-indigo-500' :
                        'bg-gray-500'}
                    `}
                    style={{ width: `${stat.percentage}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}